import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Workforce, WorkforceStatus } from "@/types/workforce";
import { WorkforceKanbanCard } from "./WorkforceKanbanCard";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";

interface WorkforceKanbanColumnProps {
	status: WorkforceStatus;
	label: string;
	items: Workforce[];
	onDrop: (workforceId: string, newStatus: WorkforceStatus) => void;
	isUpdating: string | null;
	onEdit?: (workforceId: string) => void;
}

const getStatusColors = (status: WorkforceStatus) => {
	switch (status) {
		case 'active':
			return {
				dotColor: 'bg-emerald-500',
				bgColor: 'bg-emerald-50 dark:bg-emerald-950/30',
			};
		case 'on_hold':
			return {
				dotColor: 'bg-amber-500',
				bgColor: 'bg-amber-50 dark:bg-amber-950/30',
			};
		case 'completed':
			return {
				dotColor: 'bg-blue-500',
				bgColor: 'bg-blue-50 dark:bg-blue-950/30',
			};
		case 'terminated':
			return {
				dotColor: 'bg-gray-400',
				bgColor: 'bg-gray-50 dark:bg-gray-950/30',
			};
		case 'on_bench':
			return {
				dotColor: 'bg-orange-500',
				bgColor: 'bg-orange-50 dark:bg-orange-950/30',
			};
		default:
			return {
				dotColor: 'bg-gray-500',
				bgColor: 'bg-gray-50 dark:bg-gray-950/30',
			};
	}
};

export const WorkforceKanbanColumn = ({
	status,
	label,
	items,
	onDrop,
	isUpdating,
	onEdit,
}: WorkforceKanbanColumnProps) => {
	const { t } = useTranslation();
	const [isDragOver, setIsDragOver] = useState(false);
	const [collapsed, setCollapsed] = useState(false);
	const statusColors = getStatusColors(status);

	const handleDragOver = (e: React.DragEvent) => {
		e.preventDefault();
		setIsDragOver(true);
	};

	const handleDragLeave = () => {
		setIsDragOver(false);
	};

	const handleDrop = (e: React.DragEvent) => {
		e.preventDefault();
		setIsDragOver(false);

		const workforceId = e.dataTransfer.getData('workforceId');
		if (workforceId) {
			onDrop(workforceId, status);
		}
	};

	return (
		<div
			className={cn(
				"flex-shrink-0 flex flex-col rounded-sm border border-gray-200 dark:border-gray-700 transition-all duration-300 bg-white dark:bg-gray-900",
				collapsed ? "w-10 items-center min-w-[2.5rem] max-w-[2.5rem]" : "w-72"
			)}
			style={{
				...(isDragOver ? { outline: '2px solid #3b82f6', outlineOffset: 2, background: collapsed ? undefined : 'rgba(59,130,246,0.08)' } : {})
			}}
			onDragOver={handleDragOver}
			onDragLeave={handleDragLeave}
			onDrop={handleDrop}
		>
			{collapsed ? (
				<div
					className="flex flex-col items-center w-full pt-2 cursor-pointer select-none"
					onClick={() => setCollapsed(false)}
				>
					<div className={cn("w-3 h-3 rounded-full mb-2", statusColors.dotColor)} />
					<span
						className="font-medium text-xs font-semibold text-gray-900 dark:text-gray-100"
						style={{ writingMode: 'vertical-rl', textOrientation: 'mixed' }}
					>
						{label}
					</span>
					<button
						className="mt-2 text-xs text-blue-500 hover:underline focus:outline-none"
						onClick={e => { e.stopPropagation(); setCollapsed(false); }}
						aria-label={t('common.expand')}
					>
						+
					</button>
					{items.length > 0 && (
						<div className="mt-2">
							<div className={cn(
								"rounded-sm flex items-center justify-center font-bold shadow h-6 w-6 text-[10px] text-white",
								statusColors.dotColor
							)}>
								{items.length}
							</div>
						</div>
					)}
				</div>
			) : (
				<>
					{/* Column Header */}
					<div
						className={cn(
							"px-4 py-3 border-b rounded-t-xl bg-white dark:bg-gray-900",
							isDragOver && "bg-blue-50/40 dark:bg-blue-900/30"
						)}
						style={{ borderBottom: '2px solid #e5e7eb', boxShadow: '0 2px 4px 0 rgba(0,0,0,0.03)' }}
					>
						<div className="flex justify-between items-center">
							<div className="flex items-center gap-2">
								<div className={cn("w-3 h-3 rounded-full", statusColors.dotColor)} />
								<h3 className="font-semibold text-lg tracking-tight text-gray-900 dark:text-gray-100">
									{label}
								</h3>
							</div>
							<div className="flex items-center gap-2">
								<Badge variant="secondary" className="text-xs bg-gray-100 text-gray-700 border border-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-700">
									{items.length}
								</Badge>
								<button
									className="ml-2 text-xs text-blue-500 hover:underline focus:outline-none"
									onClick={() => setCollapsed(true)}
									aria-label={t('common.collapse')}
								>
									–
								</button>
							</div>
						</div>
					</div>

					{/* Column Content */}
					<div className={cn(
						"flex-1 overflow-y-auto p-2 space-y-2 min-h-[200px] max-h-[calc(100vh-300px)]",
						isDragOver && "bg-blue-50/20 dark:bg-blue-900/20"
					)}>
						{items.length === 0 ? (
							<div className="h-20 flex flex-col items-center justify-center text-sm text-foreground/40 italic">
								<div className={cn(
									"w-8 h-8 rounded-sm border-2 border-dashed mb-2 flex items-center justify-center",
									statusColors.bgColor
								)}>
									<span className="text-xs text-muted-foreground">0</span>
								</div>
								<span className="text-center">{t("workforce.kanban.noAssignments", "No assignments")}</span>
							</div>
						) : (
							items.map((item) => (
								<WorkforceKanbanCard
									key={item.id}
									workforce={item}
									isUpdating={isUpdating === item.id}
									onEdit={onEdit}
								/>
							))
						)}
					</div>
				</>
			)}
		</div>
	);
};
